import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import {SURVEYATORIUM_API_URL} from '../api';
import FilterBuilder from '../components/FilterBuilder';
import EmailPreview from '../components/EmailPreview';

const SurveyEditor = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isNew = !id;

    const [survey, setSurvey] = useState({
        title: '',
        external_link: '',
        subject_template: '',
        body_template: '',
        filters: [],
    });
    const [participantCount, setParticipantCount] = useState(null);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const isInvited = survey.status === 'INVITED';

    const fetchSurvey = useCallback(async () => {
        if (isNew) return;
        try {
            const response = await axios.get(`${SURVEYATORIUM_API_URL}/surveys/${id}/`);
            setSurvey(response.data);
            if (response.data.filters.length > 0) {
                fetchParticipantCount(response.data.filters);
            }
        } catch (err) {
            setError('Failed to load survey data.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [id, isNew]);

    useEffect(() => {
        fetchSurvey();
    }, [fetchSurvey]);

    const fetchParticipantCount = async (filters) => {
        if (!filters || filters.length === 0) {
            setParticipantCount(null);
            return;
        }
        try {
            const response = await axios.post(`${SURVEYATORIUM_API_URL}/surveys/count/`, {filters});
            setParticipantCount(response.data.count);
        } catch (err) {
            console.error('Failed to get participant count', err);
            setParticipantCount('Error');
        }
    };

    const handleChange = (e) => {
        const {name, value} = e.target;
        setSurvey((prev) => ({...prev, [name]: value}));
    };

    const handleFilterChange = (newFilters) => {
        setSurvey((prev) => ({...prev, filters: newFilters}));
        fetchParticipantCount(newFilters);
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const payload = {...survey};
            let response;
            if (isNew) {
                response = await axios.post(`${SURVEYATORIUM_API_URL}/surveys/`, payload);
                setSuccess('Survey created successfully!');
                navigate(`/survey/${response.data.id}`);
            } else {
                response = await axios.patch(`${SURVEYATORIUM_API_URL}/surveys/${id}/`, payload);
                setSuccess('Survey saved successfully!');
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to save survey.');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const handleInvite = async () => {
        if (window.confirm(`Are you sure you want to send this survey to ${participantCount} participants? This action cannot be undone.`)) {
            setSaving(true);
            setError('');
            setSuccess('');
            try {
                await axios.post(`${SURVEYATORIUM_API_URL}/surveys/invite/`, {survey_id: id});
                setSuccess('Invitations sent successfully!');
                fetchSurvey(); // Refetch to update status and invited list
            } catch (err) {
                setError('Failed to send invitations.');
                console.error(err);
            } finally {
                setSaving(false);
            }
        }
    };

    if (loading) return <CircularProgress/>;

    return (
        <Container maxWidth="lg">
            <Paper sx={{p: 4}}>
                <Typography variant="h4" gutterBottom>
                    {isNew ? 'Create New Survey' : `Edit Survey: ${survey.title}`}
                </Typography>
                {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}

                <Grid container spacing={4} mt={1}>
                    {/* Form Fields Column */}
                    <Grid item xs={12} md={7}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    name="title"
                                    label="Survey Title"
                                    value={survey.title}
                                    onChange={handleChange}
                                    fullWidth
                                    disabled={isInvited}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="external_link"
                                    label="External Survey Link (e.g., LimeSurvey)"
                                    value={survey.external_link}
                                    onChange={handleChange}
                                    fullWidth
                                    disabled={isInvited}
                                    helperText="The base URL for your survey. The participant's unique ID will be appended automatically."
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="subject_template"
                                    label="Invitation Email Subject"
                                    value={survey.subject_template}
                                    onChange={handleChange}
                                    fullWidth
                                    disabled={isInvited}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="body_template"
                                    label="Invitation Email Body"
                                    value={survey.body_template}
                                    onChange={handleChange}
                                    fullWidth
                                    multiline
                                    rows={8}
                                    disabled={isInvited}
                                    helperText="You can use {{user.first_name}} and {{user.last_name}}. The survey link will be appended automatically."
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Live Preview Column */}
                    <Grid item xs={12} md={5}>
                        <EmailPreview
                            subjectTemplate={survey.subject_template}
                            bodyTemplate={survey.body_template}
                            externalLink={survey.external_link}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{my: 4}}>
                    <Chip label="Participant Filters"/>
                </Divider>

                <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
                    <Typography variant="h6">
                        Matching Participants: {participantCount ?? 'N/A'}
                    </Typography>
                </Box>
                <FilterBuilder
                    filters={survey.filters}
                    onFiltersChange={handleFilterChange}
                    disabled={isInvited}
                />

                <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                    {!isInvited && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={saving ? <CircularProgress size={20}/> : <SaveIcon/>}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {isNew ? 'Create Draft' : 'Save Changes'}
                        </Button>
                    )}
                    {!isNew && !isInvited && (
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={saving ? <CircularProgress size={20}/> : <SendIcon/>}
                            onClick={handleInvite}
                            disabled={!participantCount || participantCount === 0 || saving}
                        >
                            Send Invites
                        </Button>
                    )}
                </Box>

                {isInvited && (
                    <>
                        <Divider sx={{my: 4}}>
                            <Chip label="Invitation Sent" color="success"/>
                        </Divider>
                        <Typography variant="h6" gutterBottom>Invited Participants
                            ({survey.invited_user_ids.length})</Typography>
                        <Paper variant="outlined" sx={{maxHeight: 300, overflow: 'auto'}}>
                            <List dense>
                                {survey.invited_user_ids.map(uid => (
                                    <ListItem key={uid}><ListItemText primary={uid}/></ListItem>
                                ))}
                            </List>
                        </Paper>
                    </>
                )}

            </Paper>
        </Container>
    );
};

export default SurveyEditor;